"use client";

import { Flex, Text, Box as MantineBox } from "@mantine/core";
import Link from "@/components/Link";
import { formatPrice, formatNumber, generateVendorSlug } from "@/lib/shared/utils";

import styles from "./styles.module.scss";

interface TopVendor {
  id: number;
  name: string;
  region: string;
  listingCount: number;
}

interface BrandStatsBarProps {
  stats: {
    totalListings: number;
    averagePrice: number;
    medianPrice: number;
    q1Price: number;
    q3Price: number;
    minPrice: number;
    maxPrice: number;
  };
  topMarketplaces?: TopVendor[];
  topIndependentVendors?: TopVendor[];
}

const VendorLinks: React.FC<{ vendors: TopVendor[] }> = ({ vendors }) => (
  <Flex gap="xs" align="center" wrap="wrap">
    {vendors.slice(0, 5).map((v) => (
      <Link
        key={v.id}
        href={`/vendors/${generateVendorSlug(v.name)}`}
        className={styles.vendorLink}
        prefetch={false}
      >
        {v.name}
      </Link>
    ))}
  </Flex>
);

const BrandStatsBar: React.FC<BrandStatsBarProps> = ({
  stats,
  topMarketplaces,
  topIndependentVendors,
}) => {
  return (
    <MantineBox className={styles.statsBar}>
      <Flex gap="md" align="center" className={styles.statsContainer}>
        <Flex gap="xs" align="baseline" className={styles.statItem}>
          <Text className={styles.statLabel}>Items</Text>
          <Text className={styles.statValue}>{formatNumber(stats.totalListings)}</Text>
        </Flex>

        <Flex gap="xs" align="baseline" className={styles.statItem}>
          <Text className={styles.statLabel}>Median price</Text>
          <Text className={styles.statValue}>{formatPrice(stats.medianPrice)}</Text>
        </Flex>

        <Flex gap="xs" align="baseline" className={styles.statItem}>
          <Text className={styles.mutedValue} size="xs">
            50% of items between{" "}
            <Text span fw="500">
              {formatPrice(stats.q1Price)}
            </Text>{" "}
            –{" "}
            <Text span fw="500">
              {formatPrice(stats.q3Price)}
            </Text>
          </Text>
        </Flex>
      </Flex>

      {topIndependentVendors && topIndependentVendors.length > 0 && (
        <Flex
          gap="xs"
          align="baseline"
          className={styles.marketplacesRow}
          mt="md"
          direction="column"
        >
          <Text className={styles.statLabel}>Top Independants</Text>
          <VendorLinks vendors={topIndependentVendors} />
        </Flex>
      )}

      {topMarketplaces && topMarketplaces.length > 0 && (
        <Flex
          gap="xs"
          align="baseline"
          className={styles.marketplacesRow}
          mt="md"
          direction="column"
        >
          <Text className={styles.statLabel}>Top marketplaces</Text>
          <VendorLinks vendors={topMarketplaces} />
        </Flex>
      )}
    </MantineBox>
  );
};

export default BrandStatsBar;
